import { faCheck, faCopy } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, useCallback, useEffect, useState } from "react";

import { Button } from "../button/Button";
import { Tooltip } from "../tooltip/Tooltip";
import { Text } from "./Text";

import type { HTMLAttributes } from "react";
import type { VariantProps } from "tailwind-variants";
import type { textVariants } from "./Text";

export interface CopyableTextProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof textVariants> {
  /** The text content to display and copy */
  children: string;
  /** Size of the copy button */
  copyButtonSize?: "xs" | "sm" | "md" | "lg";
  /** Duration of the copy state in milliseconds */
  copyDuration?: number;
}

export const CopyableText = forwardRef<HTMLDivElement, CopyableTextProps>(
  ({ children, copyButtonSize = "xs", copyDuration = 1000, className, ...props }, ref) => {
    const [isCopied, setIsCopied] = useState(false);

    const handleCopyClick = useCallback(async () => {
      try {
        await navigator.clipboard.writeText(children);
        setIsCopied(true);
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    }, [children]);

    useEffect(() => {
      if (!isCopied) return;
      const timeout = setTimeout(() => setIsCopied(false), copyDuration);
      return () => clearTimeout(timeout);
    }, [copyDuration, isCopied]);

    return (
      <div ref={ref} {...props} className="inline-flex items-center gap-x-1">
        {isCopied && <FontAwesomeIcon className="text-secondary" icon={faCheck} />}
        <div className="truncate">
          <Text ref={ref} className={className} {...props}>
            {isCopied ? "Copied!" : children}
          </Text>
        </div>
        <div className="shrink-0">
          {!isCopied && (
            <Tooltip content="Copy">
              <Button
                isIconOnly
                size={copyButtonSize}
                variant="neutralLink"
                onClick={handleCopyClick}
              >
                <FontAwesomeIcon icon={faCopy} />
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }
);

CopyableText.displayName = "CopyableText";
